import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import { Table } from './common/Table';
import { Loading } from './common/Loading';
import { Plus } from 'lucide-react';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();

  // Debounce search input with 500ms delay
  const debouncedSearch = useDebounce(search, 500);

  const columns = [
    { key: 'firstname', label: 'First Name', sortable: true },
    { key: 'lastname', label: 'Last Name', sortable: true },
    { key: 'email', label: 'Email', sortable: false },
    { key: 'phone', label: 'Phone', sortable: true }
  ];

  useEffect(() => {
     const token = getToken();
      if (!token) {
        logout();
        navigate('/login');
        return;
      }

    const fetchCustomers = async () => {
      setError("");
      setLoading(true);
      
      try {
        const res = await axios.get(`${apiUrl}/customers`, {
          params: { search: debouncedSearch, page, limit },
          headers: { "x-auth-token": token },
        });
        if (res.data.success) {
          setCustomers(res.data.data);
          if (res.data.pagination) {
            setTotalPages(res.data.pagination.totalPages);
            setTotalRecords(res.data.pagination.total);
          }
        } else {
          setCustomers([]);
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
        if (err.response?.status === 401) {
          setError("Session expired. Please log in again.");
          logout();
          navigate('/login');
        } else if (err.response?.status === 403) {
          setError("You don't have permission to view customers.");
        } else {
          setError("Failed to fetch customers. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [debouncedSearch, page, limit, getToken, logout, navigate]);

  const handleViewOrders = (customer) => {
    navigate(`/customer/${customer.uuid}/view`);
  };

  const handleEditCustomer = (customer) => {
 
    navigate(`/customer/${customer.uuid}`);
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete ${customer.firstname} ${customer.lastname}?`)) {
      return;
    }
    
    const token = getToken();
    try {
      await axios.delete(`${apiUrl}/customers/${customer.uuid}`, {
        headers: { "x-auth-token": token },
      });
      // Refresh the customer list by removing the deleted customer
      setCustomers(customers.filter(c => c.uuid !== customer.uuid));
    } catch (err) {
      console.error("Error deleting customer:", err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      } else {
        alert("Failed to delete customer. Please try again.");
      }
    }
  };
  const handleNew = (e) => {
    navigate("/customer/new");
  }
  if (loading && customers.length === 0) {
    return <Loading text="Fetching your data..." fullScreen />;
  }

  return (
    <div className="grid p-4 grid-cols-1 shadow-lg rounded md:rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">Customers</h2>
      <div className="flex-1 pb-4"><button className='btn-primary flex items-center gap-2 whitespace-nowrap' onClick={handleNew}> <Plus size={20} /> New Customer</button></div>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button 
            onClick={() => setError("")}
            className="text-red-700 hover:text-red-900 font-bold"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-3">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-3 top-2 text-gray-500 hover:text-gray-700 font-bold"
              title="Clear search"
            >
              ✕
            </button>
          )}
          {loading && search && (
            <div className="absolute right-10 top-3 text-gray-400">
              <span className="animate-spin">⏳</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white transition"
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
        </div>
      </div>

      {customers.length === 0 && !loading ? (
        <div className="text-center py-8 text-gray-500">
          {search ? `No customers found matching "${search}"` : "No customers found"}
        </div>
      ) : (
        <>
        <Table
          columns={columns}
          data={customers}
          onRowClick={(row) => console.log('Clicked:', row.uuid)}
          expandable={true}
          renderExpandedRow={(row) => (
            <div className="flex gap-3 p-4">
              <button 
                onClick={() => handleViewOrders(row)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                View Orders
              </button>
              <button 
                onClick={() => handleEditCustomer(row)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
              >
                Edit Customer
              </button>
              <button 
                onClick={() => handleDeleteCustomer(row)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          )}
          paginated={true}
          pageSize={limit}
          rowKey="uuid"
        />
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 p-4 border-t gap-4">
          <div className="text-gray-600">
            Showing <span className="font-medium">{customers.length > 0 ? (page - 1) * limit + 1 : 0}</span> to <span className="font-medium">{Math.min(page * limit, totalRecords)}</span> of <span className="font-medium">{totalRecords}</span> records
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white"
            >
              Next
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default CustomerList;